import { queryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";

import { gqlClient } from "../../../shared/lib/gql-client";
import { IMAGE_FIELDS } from "../../images/api/fragments";
import { LINK_FIELDS } from "../../links/api/fragments";
import type { Paged, PageVariables } from "../../../shared/lib/paging";
import type { Venue } from "../types";

export const VENUE_FIELDS = gql`
  ${LINK_FIELDS}
  ${IMAGE_FIELDS}
  fragment VenueFields on Venue {
    id
    name
    addressLine
    neighbourhood
    isOutdoor
    # Plain text[] column on the row the list already reads, so selecting it
    # costs nothing extra.
    rooms
    # areaId for the form's picker; area for the name shown on the list.
    areaId
    area {
      id
      name
      # Part of the shared Venue type — an event row needs it to render times in
      # the venue's own zone, so selecting it here keeps the two consistent.
      timezone
    }
    # Both batched server-side, so a page of 20 venues is one query each.
    links {
      ...LinkFields
    }
    images {
      ...ImageFields
    }
  }
`;

const VENUES = gql`
  ${VENUE_FIELDS}
  query Venues {
    venues {
      ...VenueFields
    }
  }
`;

/**
 * The paginated list, for the venues admin page.
 *
 * Alongside `VENUES` rather than replacing it: the event form's venue picker
 * needs every room, and a picker silently missing entries is worse than a slow
 * one. The server keeps both fields for the same reason.
 */
const VENUE_PAGE = gql`
  ${VENUE_FIELDS}
  query VenuePage($limit: Int, $offset: Int) {
    venuePage(limit: $limit, offset: $offset) {
      items {
        ...VenueFields
      }
      totalCount
    }
  }
`;

const VENUE = gql`
  ${VENUE_FIELDS}
  query Venue($id: ID!) {
    venue(id: $id) {
      ...VenueFields
    }
  }
`;

export const venueQueries = {
  all: () => ["venues"] as const,
  lists: () => [...venueQueries.all(), "list"] as const,
  details: () => [...venueQueries.all(), "detail"] as const,
  /**
   * One venue, for the detail page.
   *
   * Its own query rather than picking the row out of the cached list, matching
   * `organiserQueries.detail` and for the same reason: the list is held for five
   * minutes, and a detail page opened from a stale cache would show a venue
   * someone else has since corrected. Both keys sit under `all()`, so one
   * invalidation after a save refreshes the page and the list together.
   *
   * The edit dialog deliberately still reads the *list* — see the note on its
   * route. This is additive to that, not a replacement.
   */
  detail: (id: string) =>
    queryOptions({
      queryKey: [...venueQueries.details(), id] as const,
      queryFn: () => gqlClient.request<{ venue: Venue | null }>(VENUE, { id }),
    }),
  list: () =>
    queryOptions({
      queryKey: [...venueQueries.lists()] as const,
      queryFn: () => gqlClient.request<{ venues: Venue[] }>(VENUES),
      /*
       * Reference data that changes rarely, and the event form's picker reads it
       * on every visit. Explicit invalidation after create/update keeps it
       * correct without a refetch per mount.
       */
      staleTime: 5 * 60 * 1000,
    }),

  /** One page of venues, with the total. For the list page, not the picker. */
  page: (variables: PageVariables = {}) =>
    queryOptions({
      queryKey: [...venueQueries.lists(), "page", variables] as const,
      queryFn: () =>
        gqlClient.request<{ venuePage: Paged<Venue> }>(VENUE_PAGE, variables),
      staleTime: 5 * 60 * 1000,
      placeholderData: (previous) => previous,
    }),
};
